import { RefObject, useEffect, useRef, useState } from 'react'
import * as echarts from 'components/charts/echarts'

export type EChartsOption = echarts.EChartsOption

// Initializes an ECharts instance bound to `containerRef`, keeps it in sync with
// `option`, resizes it with the container, and disposes it on unmount.
export const useECharts = (
  containerRef: RefObject<HTMLDivElement | null>,
  option: EChartsOption
): echarts.ECharts | null => {
  const [instance, setInstance] = useState<echarts.ECharts | null>(null)
  const optionRef = useRef(option)
  optionRef.current = option

  useEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }

    const chart = echarts.init(container)
    // Apply the option before exposing the instance, so consumers never get a
    // chart without a model — convertToPixel/dispatchAction throw otherwise.
    chart.setOption(optionRef.current)
    setInstance(chart)

    // ResizeObserver is unavailable in some older browsers and test/SSR
    // environments — fall back to window resize events when it's missing.
    const resize = () => chart.resize()
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null
    if (resizeObserver) {
      resizeObserver.observe(container)
    } else {
      window.addEventListener('resize', resize)
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect()
      } else {
        window.removeEventListener('resize', resize)
      }
      chart.dispose()
      setInstance(null)
    }
  }, [containerRef])

  useEffect(() => {
    // Merge (rather than replace) so interactive state owned by ECharts
    // components — most importantly the dataZoom window — survives data updates.
    instance?.setOption(option)
  }, [instance, option])

  return instance
}
